import * as React from 'react';
import styled from 'styled-components';
import { Dropdown } from '@estee/elc-dropdown';
import {
    addConfig,
    addContext,
    configConsumer,
    ITranslationsCollection,
    translate
} from '@estee/elc-service';
import { PRODUCT_AUTO_REPLENISH } from '~constants/DataTestIdAttributes';
import {
    INFO_POPUP_TRIGGER,
    FREQUENCY_REGEX,
    DAYS_REGEX,
    DAYS_PLACEHOLDER,
    DAYS
} from '~constants/AutoReplenishment';
import { AutoReplenishDropdownTheme } from '../../theme/default-theme';
import { classes } from '~utils/Classes';
import { handleKeyPress, ACCEPTABLE_KEYS_PRESSED } from '~utils/KeyPressHandler';
import { AutoReplenishmentInfoModal } from '~views/AutoReplenishmentInfoModal';
import { AutoReplenishmentViewController } from '~controllers/AutoReplenishmentViewController';
import { TIMING_TYPE } from '~constants/AutoReplenishmentTimingTypes';
import { observer } from 'mobx-react';
import { mergeConfigs } from '~utils/ConfigUtils';
import { ToggleSelector, IToggleSelector } from '~views/common/ToggleSelector';

export interface IAutoReplenishment {
    className?: string;
    translations?: ITranslationsCollection;
    viewController: AutoReplenishmentViewController;
    config: IAutoReplenishConfig;
}
export interface IAutoReplenishConfig {
    autoReplenishment?: IAutoReplenishOptions;
    onSelect(option: number): void;
}

// TODO: eventually, all AR options should be moved here
export interface IAutoReplenishOptions {
    enabled?: boolean;
    intervals?: number[];
    defaultInterval?: number;
    toggleType?: string;
    timingType?: TIMING_TYPE;
    infoPopupTriggerType?: INFO_POPUP_TRIGGER;
    infoPopupNodeId?: number;
}
export interface IIsAutoReplenishSelected {
    isAutoReplenishSelected: boolean;
}

export interface IFocus {
    isFocused: boolean;
}

const AutoReplenishWrapper = styled.div`
    width: 100%;
`;

const SubscribeWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
`;

const SubscribeCopyWrapper = styled.div`
    display: block !important;
`;

const AutoReplenishDropdown = styled(Dropdown)`
    opacity: ${(props: IIsAutoReplenishSelected) => (props.isAutoReplenishSelected ? 1 : 0.5)};
    transition: opacity 1s ease;
    z-index: 999;
    ${AutoReplenishDropdownTheme};
    .elc-dropdown-options {
        z-index: 999;
    }
`;

const AutoReplenishDropdownWrapper = styled.div`
    display: block;
`;

const AutoReplenishNonToggleableArea = styled.div`
    display: inline-block;
`;

@addContext()
@addConfig(['subscriptionsConfig'], mergeConfigs)
@configConsumer(['autoReplenishment', 'onSelect'])
@translate(['autoReplenishLabel', 'selectFrequencyLabel', 'subscribeCopyLabel', 'subscribeLabel'])
@observer
class AutoReplenishment extends React.Component<IAutoReplenishment> {
    public static displayName: string;

    constructor(props: IAutoReplenishment) {
        super(props);
        props.viewController.initialize(props);
    }

    private stopClickPropagation = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();
    };

    private handleOnChangeDropDown = (inputName: string, val: string) => {
        this.props.viewController.setAutoReplenishOption(parseInt(val, 10));
    };

    private mapOptions =
        (autoReplenishLabel: string, timingType?: TIMING_TYPE) => (option: number) => {
            const label = autoReplenishLabel.toLowerCase().includes(DAYS_PLACEHOLDER)
                ? autoReplenishLabel.replace(DAYS_REGEX, option.toString())
                : autoReplenishLabel.replace(FREQUENCY_REGEX, option.toString());

            const value = timingType ? (option * DAYS[timingType]).toString() : option.toString();

            return {
                label,
                value
            };
        };

    private getSelectedValue = (
        selectedAutoReplenishOption: number,
        selectFrequencyLabel: string,
        autoReplenishLabel: string,
        timingType?: TIMING_TYPE
    ): string => {
        let selectedValue: string;

        if (selectedAutoReplenishOption === 0) {
            selectedValue = selectFrequencyLabel;
        } else if (autoReplenishLabel.toLowerCase().includes(DAYS_PLACEHOLDER)) {
            selectedValue = autoReplenishLabel.replace(
                DAYS_REGEX,
                selectedAutoReplenishOption.toString()
            );
        } else {
            selectedValue = autoReplenishLabel.replace(
                FREQUENCY_REGEX,
                timingType
                    ? Math.round(selectedAutoReplenishOption / DAYS[timingType]).toString()
                    : selectedAutoReplenishOption.toString()
            );
        }

        return selectedValue;
    };

    public render() {
        const { className, translations, viewController } = this.props;
        const enabled =
            viewController.data.componentConfig && viewController.data.componentConfig.enabled;

        if (!enabled) {
            return null;
        }

        const {
            data: {
                isAutoReplenishSelected,
                selectedAutoReplenishOption,
                intervals,
                toggleType,
                timingType
            },
            infoModalConfig,
            setAutoReplenishSelection,
            toggleAutoReplenishSelection
        } = viewController;

        const { autoReplenishLabel, selectFrequencyLabel, subscribeCopyLabel } =
            translations as ITranslationsCollection;

        const productAutoReplenishWrapperClassnames = classes(
            'product-auto-replenish-wrapper',
            className
        );
        const autoReplenishSubscribeWrapperClassnames = classes('auto-replenish-subscribe-wrapper');
        const autoReplenishSubscribeCopyWrapperClassnames = classes(
            'auto-replenish-subscribe-copy-wrapper'
        );
        const autoReplenishDropdownWrapperClassnames = classes('auto-replenish-dropdown-wrapper');
        const autoReplenishDropdownClassNames = classes('auto-replenish-dropdown');

        const dropdownOptions = intervals.map(this.mapOptions(autoReplenishLabel, timingType));

        const selectedValue = this.getSelectedValue(
            selectedAutoReplenishOption,
            selectFrequencyLabel,
            autoReplenishLabel,
            timingType
        );

        const toggleAutoReplenish = (isChecked: boolean) => {
            setAutoReplenishSelection(!isChecked);
        };

        const acceptedKeys = [ACCEPTABLE_KEYS_PRESSED.ENTER, ACCEPTABLE_KEYS_PRESSED.SPACEBAR];

        const toggleSelectorProps: IToggleSelector = {
            toggleType,
            translations,
            toggleAutoReplenish,
            isAutoReplenishSelected,
            setAutoReplenishSelection
        };

        const onKeyPressed = (keyPressed: React.KeyboardEvent<HTMLDivElement>) => {
            handleKeyPress(keyPressed, acceptedKeys, toggleAutoReplenishSelection);
        };

        return (
            <AutoReplenishWrapper
                className={productAutoReplenishWrapperClassnames}
                onClick={toggleAutoReplenishSelection}
                aria-label={selectedValue}
            >
                <SubscribeWrapper
                    onKeyPress={onKeyPressed}
                    className={autoReplenishSubscribeWrapperClassnames}
                >
                    <ToggleSelector {...toggleSelectorProps} />
                </SubscribeWrapper>
                <SubscribeCopyWrapper className={autoReplenishSubscribeCopyWrapperClassnames}>
                    {subscribeCopyLabel}
                </SubscribeCopyWrapper>
                <AutoReplenishDropdownWrapper className={autoReplenishDropdownWrapperClassnames}>
                    <AutoReplenishNonToggleableArea onClick={this.stopClickPropagation}>
                        <AutoReplenishDropdown
                            className={autoReplenishDropdownClassNames}
                            isAutoReplenishSelected={isAutoReplenishSelected}
                            options={dropdownOptions}
                            name={'autoReplenishOptions'}
                            selectedValue={selectedValue}
                            dataTestId={PRODUCT_AUTO_REPLENISH}
                            onChange={this.handleOnChangeDropDown}
                            isDisabled={!isAutoReplenishSelected}
                        />
                    </AutoReplenishNonToggleableArea>
                    <AutoReplenishmentInfoModal config={infoModalConfig} />
                </AutoReplenishDropdownWrapper>
            </AutoReplenishWrapper>
        );
    }
}

AutoReplenishment.displayName = 'AutoReplenishment';

export default AutoReplenishment;
